import React, { useCallback, useContext, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button, { LinkButton } from '@atlaskit/button/new';
import type { PopupProps } from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { LOOM_CROSS_SELL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';

import { useDiscoverabilityPulseActions } from '../DiscoverabilityPulse/useDiscoverabilityPulseState.ts';
import { useStreamliningCrossJoin } from '../services/useStreamliningCrossJoin.tsx';
import { useLoomCrossJoinUrl } from '../useLoomCrossJoinUrl.ts';

import {
	BaseLoomPopupCard,
	type EntryPointId,
	type TriggerProps as BaseTriggerProps,
} from './BaseLoomPopupCard.tsx';

const sourceAdType = 'crossJoin';

export const i18n = defineMessages({
	crossJoinCTA: {
		id: 'loom-utils.loom-cross-sell-popup-card.crossJoinCTA',
		defaultMessage: 'Try now',
		description: 'text on button that launches a cross-join flow for Loom',
	},
	requestAccessSuccessFlagTitle: {
		id: 'loom-utils.loom-cross-join-popup-card.request-access-success-flag.title',
		defaultMessage: 'Your admins were notified of your request',
		description: 'Title for the success flag after requesting access to Loom',
	},
	requestAccessSuccessFlagDescription: {
		id: 'loom-utils.loom-cross-join-popup-card.request-access-success-flag.description',
		defaultMessage: 'You’ll receive an email once they’ve approved it.',
		description: 'Description for the success flag after requesting access to Loom',
	},
	requestAccessErrorFlagTitle: {
		id: 'loom-utils.loom-cross-join-popup-card.request-access-error-flag.title',
		defaultMessage: "We couldn't add you to Loom",
		description: 'Title for the error flag after requesting access to Loom',
	},
	requestAccessErrorFlagDescription: {
		id: 'loom-utils.loom-cross-join-popup-card.request-access-error-flag.description',
		defaultMessage: "Try again in a few minutes. If you still aren't able to join, ",
		description: 'Description for the error flag after requesting access to Loom',
	},
	requestAccessErrorFlagActionTitle: {
		id: 'loom-utils.loom-cross-join-popup-card.request-access-error-flag.action.title',
		defaultMessage: 'contact support',
		description: 'Action to contact support',
	},
});

type TriggerProps = BaseTriggerProps & {
	onClick: () => void;
};

export type LoomCrossJoinPopupCardProps = {
	description: string;
	entryPointId: EntryPointId;
	placement?: PopupProps['placement'];
	shouldRenderToParent?: boolean;
	// action subject id of the entrypoint the popup is triggered from
	sourceActionSubjectId: string;
	title: string;
	triggerLink: (href: string, triggerProps: TriggerProps) => ReactNode;
	flags?: FlagsStateContainer;
	sourceComponent?: string;
};

export const LoomCrossJoinPopupCard: React.FC<LoomCrossJoinPopupCardProps> = ({
	description,
	entryPointId,
	placement,
	shouldRenderToParent,
	sourceActionSubjectId,
	sourceComponent,
	title,
	triggerLink,
	flags,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();

	const crossJoinHref = useLoomCrossJoinUrl();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { stopPulse } = useDiscoverabilityPulseActions();
	const streamliningCrossJoinOnButtonClicked = useStreamliningCrossJoin({
		entryPointId,
		flags,
		source: 'loomCrossJoinPopupCard',
		sourceAdType,
		sourceComponent,
	});

	const stopDiscoverabilityPulse = useCallback(() => {
		void stopPulse();
	}, [stopPulse]);

	const onCrossJoinSuccess = useCallback(() => {
		stopDiscoverabilityPulse();

		experienceTracker.succeed({
			name: LOOM_CROSS_SELL_EXPERIENCE,
			attributes: {
				sourceComponent: entryPointId,
				adType: sourceAdType,
			},
		});
	}, [entryPointId, experienceTracker, stopDiscoverabilityPulse]);

	const onTryNowButtonClick = useCallback(async () => {
		if (fg('twcg-1241_streamlining_loom_cross_join_gate')) {
			await streamliningCrossJoinOnButtonClicked(false);
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'tryNow',
				source: 'loomCrossJoinPopupCard',
				attributes: {
					adType: sourceAdType,
					entrypoint: entryPointId,
					sourceComponent,
				},
			},
		}).fire();

		onCrossJoinSuccess();
	}, [
		createAnalyticsEvent,
		entryPointId,
		onCrossJoinSuccess,
		sourceComponent,
		streamliningCrossJoinOnButtonClicked,
	]);

	const onCrossJoinTriggerClick = useCallback(async () => {
		if (fg('twcg-1241_streamlining_loom_cross_join_gate')) {
			await streamliningCrossJoinOnButtonClicked(true);
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: sourceActionSubjectId,
				source: sourceActionSubjectId,
				attributes: {
					adType: sourceAdType,
					entrypoint: entryPointId,
					sourceComponent,
				},
			},
		}).fire();

		onCrossJoinSuccess();
	}, [
		createAnalyticsEvent,
		entryPointId,
		onCrossJoinSuccess,
		sourceActionSubjectId,
		sourceComponent,
		streamliningCrossJoinOnButtonClicked,
	]);

	return (
		<BaseLoomPopupCard
			actions={
				fg('twcg-1241_streamlining_loom_cross_join_gate') ? (
					<Button appearance="discovery" onClick={onTryNowButtonClick}>
						{intl.formatMessage(i18n.crossJoinCTA)}
					</Button>
				) : (
					<LinkButton
						appearance="discovery"
						href={crossJoinHref}
						onClick={onTryNowButtonClick}
						rel="noreferrer"
						target="_blank"
					>
						{intl.formatMessage(i18n.crossJoinCTA)}
					</LinkButton>
				)
			}
			description={description}
			entryPointId={entryPointId}
			placement={placement}
			shouldRenderToParent={shouldRenderToParent}
			title={title}
			trigger={(popupTriggerProps) =>
				// the link button behaviour is handled in streamliningCrossJoinOnButtonClicked
				// when feature gate is on for Loom crossjoin experiment
				triggerLink(!fg('twcg-1241_streamlining_loom_cross_join_gate') ? crossJoinHref : '', {
					...popupTriggerProps,
					onClick: onCrossJoinTriggerClick,
				})
			}
			sourceAdType={sourceAdType}
			sourceComponent={sourceComponent}
		/>
	);
};
